import { useMentionCards } from "@/contexts/MentionCardsContext";
import { isBefore } from "date-fns";
import { InfoIcon } from "lucide-react";
import { useRef, useState } from "react";
import {
  generatePath,
  useHistory,
  useParams,
  useRouteMatch,
} from "react-router-dom";

import { hideIrrelevantMentions } from "@/api/endpoints/searches";
import { postTrackingData } from "@/api/endpoints/track";
import { URLS } from "@/api/urls";
import DotsHorizontal from "@/assets/icons/dots-horizontal.svg";
import DownloadIcon from "@/assets/icons/download.svg";
import MailIcon from "@/assets/icons/envelope.svg";
import ShareIcon from "@/assets/icons/share.svg";
import Button from "@/components/Button";
import Spinner from "@/components/Spinner";
import Toggle from "@/components/Toggle";
import { Button as UIButton } from "@/components/ui/button";
import { CALENDAR_START_DATE_DEFAULT } from "@/constants";
import Initializing from "@/containers/Initializing";
import { FEATURE } from "@/features";
import useFilterParams from "@/hooks/useFilterParams";
import { useHasFeature } from "@/hooks/useHasFeature";
import { useOrganization } from "@/hooks/useOrganization";
import { useUser } from "@/hooks/useUser";
import { RELATIVE_PATHS } from "@/routes_new";
import { EVENT } from "@/tracking_events";
import { cx, stringifyUrl } from "@/utils";
import QuickSubscribe from "@/views/MentionsView/partials/QuickSubscribe";
import AllEarsTitle from "@components/AllEarsTitle";
import Container from "@components/Container";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "@components/Dropdown";
import TextButton from "@components/TextButton";
import Typography from "@components/Typography";

import {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
} from "@components/ui/alert";
import { i18n } from "@lingui/core";
import { Trans, useLingui } from "@lingui/react/macro";
import FilterButton from "../FilterButton";
import AlertExportModal from "../partials/AlertExportModal";
import AlertShareModal from "../partials/AlertShareModal";
import DateRangeButton from "../partials/DateRangeButton";
import SortButton from "../partials/SortButton";
import TopWidgets from "../partials/TopWidgets";
import styles from "./MentionLayout.module.css";
import { MENTION_VIEW_DATE_RANGES } from "./ranges";

export type MentionLayoutProps = { children: React.ReactNode; url: string };

const MentionLayout: React.FunctionComponent<MentionLayoutProps> = ({
  children,
  url,
}) => {
  const { t } = useLingui();
  const { uuid } = useParams<{ uuid: string }>();
  const { data: user } = useUser();
  const { id: user_id = "" } = user?.ok ? user.data : {};
  const { data: orgData } = useOrganization();
  const { uuid: organization_uuid = "" } = orgData?.ok ? orgData.data : {};
  const { alert, meta } = useMentionCards();
  const [isSharing, setIsSharing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const isInsightsPage =
    useRouteMatch(`${url}${RELATIVE_PATHS.ALERT_INSIGHTS}/`)?.isExact || false;
  const history = useHistory();

  const [filters] = useFilterParams();
  const hasPptFeature = useHasFeature(FEATURE.HAS_PPT_FEATURE);

  const isPollInitiated = useRef(false);

  if (alert?.is_initializing && !isPollInitiated.current) {
    isPollInitiated.current = true;
    const checkIfSearchHasFinishedLoading = async () => {
      try {
        const response = await fetch(
          generatePath(URLS.SEARCHES_DETAIL, { uuid })
        );
        const data = await response.json();

        if (data?.status === "idle") {
          window.location.reload();
        } else {
          setTimeout(checkIfSearchHasFinishedLoading, 10000); // Poll with regular intervals
        }
      } catch {
        // Do nothing on error
      }
    };

    checkIfSearchHasFinishedLoading();
  }
  // Ranges for the datepicker
  const ranges = [
    {
      startDate: alert
        ? new Date(alert.start_date)
        : new Date(CALENDAR_START_DATE_DEFAULT),
      endDate: undefined,
      label: t`Alla träffar`,
    },
    ...MENTION_VIEW_DATE_RANGES.map(({ label, ...rest }) => ({
      label: i18n._(label),
      ...rest,
    })),
  ];

  const archived = alert
    ? alert.end_date && isBefore(new Date(alert.end_date), new Date())
    : false;

  return (
    <>
      <AllEarsTitle title={alert?.title} />
      {!alert ? (
        <div className="flex items-center justify-center h-full">
          <Spinner />
        </div>
      ) : !alert?.can_mentions_be_presented ? (
        <Initializing alert={alert} />
      ) : (
        <>
          {alert?.is_initializing == true ? (
            <Container>
              <Typography
                margin="none"
                variant="h6"
                wordBreak
                data-testid="mentions-loading"
              >
                <div
                  className={styles.spinner}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <Spinner
                    style={{
                      width: "20px",
                      height: "20px",
                      marginTop: "-2px",
                    }}
                  />
                  {t`Söker efter nya omnämnanden.`}
                </div>
              </Typography>
            </Container>
          ) : null}
          <Container fullWidth className={cx(styles.row, styles.header)}>
            {alert?.title == null ? null : (
              <Typography
                margin="none"
                variant="h1"
                wordBreak
                data-testid="mentions-title"
              >
                {alert.title}
              </Typography>
            )}

            <div className={styles.rightRow}>
              <Dropdown>
                <DropdownToggle>
                  <TextButton
                    icon={DotsHorizontal}
                    className={styles.settingsToggle}
                  />
                </DropdownToggle>
                <DropdownMenu>
                  <DropdownItem>
                    {isInsightsPage ? (
                      <TextButton color="inherit" icon={ShareIcon}>
                        {t`Dela analys`}
                      </TextButton>
                    ) : (
                      <TextButton
                        color="inherit"
                        icon={ShareIcon}
                        onClick={() => {
                          setIsSharing(true);
                        }}
                      >
                        {t`Dela`}
                      </TextButton>
                    )}
                  </DropdownItem>
                  <DropdownItem>
                    {organization_uuid && (
                      <TextButton
                        color="inherit"
                        icon={MailIcon}
                        href={generatePath(
                          `${url}${RELATIVE_PATHS.ALERT_SETTINGS}/`,
                          {
                            uuid,
                            orgUuid: organization_uuid,
                          }
                        )}
                      >
                        {t`Prenumerera`}
                      </TextButton>
                    )}
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>

              <Button
                className={styles.share}
                data-testid="open-share-modal"
                onClick={() => {
                  postTrackingData(EVENT.SHARE_ALERT);
                  setIsSharing(true);
                }}
                variant="tertiary"
                icon={ShareIcon}
              >
                {t`Dela`}
              </Button>

              <Button
                className={styles.export}
                data-testid="open-export-modal"
                onClick={() => {
                  postTrackingData(EVENT.EXPORT_ALERT);
                  setIsExporting(true);
                }}
                variant="tertiary"
                icon={DownloadIcon}
              >
                {t`Exportera`}
              </Button>

              {!archived && user_id && organization_uuid && (
                <QuickSubscribe
                  className={styles.subscribe}
                  user_id={user_id}
                  organization_uuid={organization_uuid}
                  search_uuid={uuid}
                />
              )}
            </div>
          </Container>

          <Container fullWidth className={styles.toggleRow}>
            <FilterButton />
            <div className={cx(styles.row, styles.dateAndSort)}>
              <DateRangeButton
                key={alert.uuid}
                ranges={ranges}
                settings={{
                  min: alert?.start_date
                    ? new Date(alert.start_date)
                    : undefined,
                }}
              />
              <SortButton />
            </div>
            <div className={styles.toggle}>
              <Toggle
                onChange={() => {
                  postTrackingData(EVENT.TOGGLE_ALERT_ANALYTICS, {
                    to: isInsightsPage ? "alert" : "analytics",
                  });
                  isInsightsPage
                    ? history.replace(
                        stringifyUrl({
                          url: generatePath(url, {
                            uuid: alert.uuid,
                            orgUuid: organization_uuid,
                          }),
                          query: filters,
                        })
                      )
                    : history.replace(
                        stringifyUrl({
                          url: generatePath(
                            `${url}${RELATIVE_PATHS.ALERT_INSIGHTS}/`,
                            {
                              orgUuid: organization_uuid,
                              uuid: alert.uuid,
                            }
                          ),
                          query: filters,
                        })
                      );
                }}
                checked={isInsightsPage}
                untoggledLabel={t`Omnämnanden`}
                toggledLabel={t`Analys`}
              />
            </div>
          </Container>

          <TopWidgets
            isInsightsPage={isInsightsPage}
            alertUuid={uuid}
            filters={filters}
            url={url}
          />

          {meta?.llm_irrelevant_mentions_count ? (
            <Container>
              <Alert>
                <InfoIcon />
                <AlertTitle>
                  {meta?.llm_irrelevant_mentions_count}{" "}
                  <Trans>omnämnanden med låg relevans</Trans>
                </AlertTitle>
                <AlertDescription>
                  <Trans>Bedömt utifrån frågan</Trans>: &quot;
                  {alert?.information_need}
                  &quot;
                </AlertDescription>
                <AlertAction>
                  <UIButton
                    size="xs"
                    variant="outline"
                    onClick={async () => {
                      const res = await hideIrrelevantMentions(uuid);
                      if (res.ok) window.location.reload();
                    }}
                  >
                    <Trans>Dölj alla</Trans>
                  </UIButton>
                </AlertAction>
              </Alert>
            </Container>
          ) : null}
          {children}
        </>
      )}

      <AlertShareModal
        filters={filters}
        show={isSharing}
        onHide={() => {
          setIsSharing(false);
        }}
      />

      <AlertExportModal
        filters={filters}
        hasPptFeature={hasPptFeature}
        reportUuid={alert?.insights_report_uuid}
        show={isExporting}
        onHide={() => {
          setIsExporting(false);
        }}
      />
    </>
  );
};

export default MentionLayout;
